import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Mail, Users, Copy, Trash2, Check, FileText, Send, RefreshCw, MapPin, Calendar, Megaphone, UserPlus, MessageSquare, Eye, Edit3, ChevronDown, ChevronUp, History, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { BACKEND_URL } from '@/utils/apiConfig';

const API = `${BACKEND_URL}/api`;

const NewsletterManager = ({ token }) => {
  const [activeTab, setActiveTab] = useState("subscribers"); // subscribers | compose | history
  const [subscribers, setSubscribers] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0 });
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  // Compose state
  const [composeData, setComposeData] = useState(null);
  const [composeLoading, setComposeLoading] = useState(false);
  const [editableSubject, setEditableSubject] = useState("");
  const [editableIntro, setEditableIntro] = useState("");
  const [editableSections, setEditableSections] = useState({
    promotions: true,
    location: true,
    trips: true,
    members: true,
    threads: true,
  });
  const [editableCustom, setEditableCustom] = useState("");
  const [sending, setSending] = useState(false);
  const [preview, setPreview] = useState(false);
  
  // History state
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Migration
  const [migrating, setMigrating] = useState(false);

  useEffect(() => {
    fetchSubscribers();
  }, []);

  const fetchSubscribers = async () => {
    try {
      const res = await axios.get(`${API}/admin/newsletter/subscribers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscribers(res.data.subscribers);
      setStats({ total: res.data.total, active: res.data.active });
    } catch (error) {
      toast.error("Failed to load subscribers");
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveSubscriber = async (id) => {
    if (!confirm("Remove this subscriber?")) return;
    try {
      await axios.delete(`${API}/admin/newsletter/subscribers/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Subscriber removed");
      fetchSubscribers();
    } catch (error) {
      toast.error("Failed to remove subscriber");
    }
  };

  const handleCopyAllEmails = () => {
    const activeEmails = subscribers
      .filter(s => s.is_active)
      .map(s => s.email)
      .join(", ");
    if (!activeEmails) {
      toast.error("No active subscribers to copy");
      return;
    }
    navigator.clipboard.writeText(activeEmails).then(() => {
      setCopied(true);
      toast.success(`Copied ${stats.active} email(s) to clipboard!`);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => toast.error("Failed to copy emails"));
  };

  const handleMigrateMembers = async () => {
    if (!confirm("This will subscribe all existing verified members to the newsletter. Continue?")) return;
    setMigrating(true);
    try {
      const res = await axios.post(`${API}/admin/newsletter/migrate-members`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Migration complete: ${res.data.subscribed} new subscribers, ${res.data.skipped} already subscribed`);
      fetchSubscribers();
    } catch (error) {
      toast.error("Migration failed");
    } finally {
      setMigrating(false);
    }
  };

  // --- Compose ---
  const handleCompose = async () => {
    setComposeLoading(true);
    try {
      const res = await axios.get(`${API}/admin/newsletter/compose`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setComposeData(res.data);
      setEditableSubject("George Dives Newsletter");
      setEditableIntro("Here's what's been happening in the George Dives community!");
      setEditableSections({
        promotions: res.data.active_promotions?.length > 0,
        location: res.data.location_changed,
        trips: res.data.new_trips?.length > 0,
        members: res.data.new_members?.length > 0,
        threads: res.data.recent_threads?.length > 0,
      });
      setEditableCustom("");
      setPreview(false);
      setActiveTab("compose");
    } catch (error) {
      toast.error("Failed to load newsletter data");
    } finally {
      setComposeLoading(false);
    }
  };

  const buildNewsletterHTML = () => {
    if (!composeData) return "";
    let html = "";

    // Intro
    html += `<p style="color: #334155; font-size: 15px; line-height: 1.6; margin: 0 0 20px 0;">${editableIntro}</p>`;

    // Custom content
    if (editableCustom.trim()) {
      html += `<div style="margin: 0 0 24px 0; padding: 16px; background: #f0f9ff; border-radius: 8px; border-left: 4px solid #0ea5e9;">
        <p style="color: #334155; font-size: 14px; line-height: 1.6; margin: 0; white-space: pre-wrap;">${editableCustom}</p>
      </div>`;
    }

    // Location
    if (editableSections.location && composeData.current_location) {
      const loc = composeData.current_location;
      html += `<div style="margin: 0 0 24px 0;">
        <h3 style="color: #0ea5e9; font-size: 16px; margin: 0 0 8px 0; display: flex; align-items: center; gap: 6px;">
          <span style="font-size: 18px;">📍</span> ${composeData.location_changed ? "We've Moved!" : "Current Location"}
        </h3>
        <div style="padding: 12px 16px; background: #f8fafc; border-radius: 8px;">
          <p style="color: #334155; font-size: 15px; font-weight: 600; margin: 0 0 4px 0;">${loc.name}, ${loc.country}</p>
          ${loc.description ? `<p style="color: #64748b; font-size: 13px; margin: 0;">${loc.description}</p>` : ""}
        </div>
      </div>`;
    }

    // Promotions
    if (editableSections.promotions && composeData.active_promotions?.length > 0) {
      html += `<div style="margin: 0 0 24px 0;">
        <h3 style="color: #0ea5e9; font-size: 16px; margin: 0 0 8px 0;">
          <span style="font-size: 18px;">🎉</span> Current Promotions
        </h3>`;
      composeData.active_promotions.forEach(p => {
        html += `<div style="padding: 12px 16px; background: #fffbeb; border-radius: 8px; margin-bottom: 8px; border-left: 4px solid #f59e0b;">
          <p style="color: #92400e; font-size: 14px; font-weight: 600; margin: 0 0 4px 0;">${p.title}</p>
          <p style="color: #78350f; font-size: 13px; margin: 0;">${p.content}</p>
        </div>`;
      });
      html += `</div>`;
    }

    // Trips
    if (editableSections.trips && composeData.new_trips?.length > 0) {
      html += `<div style="margin: 0 0 24px 0;">
        <h3 style="color: #0ea5e9; font-size: 16px; margin: 0 0 8px 0;">
          <span style="font-size: 18px;">🤿</span> Upcoming Trip Proposals
        </h3>`;
      composeData.new_trips.forEach(t => {
        const dates = t.proposed_dates?.length > 0 ? t.proposed_dates.join(", ") : "Dates TBD";
        html += `<div style="padding: 12px 16px; background: #ecfdf5; border-radius: 8px; margin-bottom: 8px; border-left: 4px solid #10b981;">
          <p style="color: #065f46; font-size: 14px; font-weight: 600; margin: 0 0 4px 0;">${t.title}</p>
          <p style="color: #047857; font-size: 13px; margin: 0;">${t.location}, ${t.country} &middot; ${dates}</p>
          ${t.description ? `<p style="color: #064e3b; font-size: 12px; margin: 4px 0 0 0;">${t.description}</p>` : ""}
        </div>`;
      });
      html += `</div>`;
    }

    // New members
    if (editableSections.members && composeData.new_members?.length > 0) {
      html += `<div style="margin: 0 0 24px 0;">
        <h3 style="color: #0ea5e9; font-size: 16px; margin: 0 0 8px 0;">
          <span style="font-size: 18px;">👋</span> Welcome New Members
        </h3>
        <p style="color: #334155; font-size: 14px; margin: 0;">`;
      const memberNames = composeData.new_members.map(m => m.name).filter(Boolean);
      html += memberNames.length > 5
        ? `${memberNames.slice(0, 5).join(", ")} and ${memberNames.length - 5} more!`
        : memberNames.join(", ");
      html += ` joined the community.</p></div>`;
    }

    // Forum threads
    if (editableSections.threads && composeData.recent_threads?.length > 0) {
      html += `<div style="margin: 0 0 24px 0;">
        <h3 style="color: #0ea5e9; font-size: 16px; margin: 0 0 8px 0;">
          <span style="font-size: 18px;">💬</span> Hot Forum Topics
        </h3>`;
      composeData.recent_threads.forEach(t => {
        html += `<div style="padding: 8px 16px; background: #f8fafc; border-radius: 6px; margin-bottom: 6px;">
          <p style="color: #334155; font-size: 13px; margin: 0;"><strong>${t.title}</strong> by ${t.author_name} &middot; ${t.reply_count || 0} replies</p>
        </div>`;
      });
      html += `</div>`;
    }

    return html;
  };

  const handleSendNewsletter = async () => {
    if (!editableSubject.trim()) {
      toast.error("Please enter a subject");
      return;
    }
    const content = buildNewsletterHTML();
    if (!content.trim()) {
      toast.error("Newsletter content is empty");
      return;
    }
    if (!confirm(`Send this newsletter to ${composeData?.subscriber_count || 0} active subscribers?`)) return;

    setSending(true);
    try {
      const res = await axios.post(
        `${API}/admin/newsletter/send?subject=${encodeURIComponent(editableSubject)}&content=${encodeURIComponent(content)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Newsletter sent to ${res.data.sent} subscribers!`);
      setActiveTab("subscribers");
      setComposeData(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send newsletter");
    } finally {
      setSending(false);
    }
  };

  // --- History ---
  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      const res = await axios.get(`${API}/admin/newsletter/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistory(res.data);
    } catch (error) {
      toast.error("Failed to load history");
    } finally {
      setHistoryLoading(false);
    }
  };

  // --- Render ---
  return (
    <div className="space-y-6">
      {/* Tab Navigation */}
      <div className="flex gap-2 flex-wrap">
        <Button
          variant={activeTab === "subscribers" ? "default" : "outline"}
          onClick={() => setActiveTab("subscribers")}
          size="sm"
          data-testid="newsletter-tab-subscribers"
        >
          <Users className="w-4 h-4 mr-1" />
          Subscribers
        </Button>
        <Button
          variant={activeTab === "compose" ? "default" : "outline"}
          onClick={handleCompose}
          size="sm"
          disabled={composeLoading}
          data-testid="newsletter-tab-compose"
        >
          {composeLoading ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <FileText className="w-4 h-4 mr-1" />}
          Compose Newsletter
        </Button>
        <Button
          variant={activeTab === "history" ? "default" : "outline"}
          onClick={() => { setActiveTab("history"); fetchHistory(); }}
          size="sm"
          data-testid="newsletter-tab-history"
        >
          <History className="w-4 h-4 mr-1" />
          History
        </Button>
      </div>

      {/* ===== SUBSCRIBERS TAB ===== */}
      {activeTab === "subscribers" && (
        <>
          {/* Stats */}
          <div className="grid grid-cols-2 gap-3 sm:gap-4">
            <Card>
              <CardContent className="p-4 sm:pt-6">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="bg-cyan-100 p-2 sm:p-3 rounded-full shrink-0">
                    <Users className="w-5 h-5 sm:w-6 sm:h-6 text-cyan-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xl sm:text-2xl font-bold">{stats.total}</p>
                    <p className="text-xs sm:text-sm text-gray-500 truncate">Subscribers</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 sm:pt-6">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="bg-green-100 p-2 sm:p-3 rounded-full shrink-0">
                    <Mail className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xl sm:text-2xl font-bold">{stats.active}</p>
                    <p className="text-xs sm:text-sm text-gray-500">Active</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Migration Button */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Migrate Existing Members</p>
                  <p className="text-sm text-gray-500">Subscribe all verified members who aren't already on the list</p>
                </div>
                <Button onClick={handleMigrateMembers} disabled={migrating} variant="outline" size="sm" data-testid="migrate-members-btn">
                  {migrating ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <UserPlus className="w-4 h-4 mr-1" />}
                  {migrating ? "Migrating..." : "Migrate"}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Subscribers List */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Subscribers</CardTitle>
              {stats.active > 0 && (
                <Button
                  onClick={handleCopyAllEmails}
                  variant="outline"
                  size="sm"
                  className={copied ? "bg-green-50 border-green-300 text-green-700" : ""}
                >
                  {copied ? (
                    <><Check className="w-4 h-4 mr-2" /> Copied!</>
                  ) : (
                    <><Copy className="w-4 h-4 mr-2" /> Copy All Emails ({stats.active})</>
                  )}
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {loading ? (
                <p className="text-center py-8 text-gray-500">Loading...</p>
              ) : subscribers.length === 0 ? (
                <p className="text-center py-8 text-gray-500">No subscribers yet</p>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {subscribers.map((sub) => (
                    <div
                      key={sub.id}
                      className={`flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 border rounded-lg gap-2 ${sub.is_active ? "bg-white" : "bg-gray-100"}`}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{sub.email}</p>
                        {sub.name && <p className="text-sm text-gray-500">{sub.name}</p>}
                        <p className="text-xs text-gray-400">
                          Subscribed: {new Date(sub.subscribed_at).toLocaleDateString()}
                          {sub.source && <span className="ml-2 text-cyan-600">({sub.source})</span>}
                          {!sub.is_active && sub.unsubscribed_at && (
                            <span className="ml-2 text-red-500">(Unsubscribed: {new Date(sub.unsubscribed_at).toLocaleDateString()})</span>
                          )}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className={`px-2 py-0.5 rounded text-xs ${sub.is_active ? "bg-green-100 text-green-700" : "bg-gray-200 text-gray-600"}`}>
                          {sub.is_active ? "Active" : "Inactive"}
                        </span>
                        <Button size="sm" variant="ghost" onClick={() => handleRemoveSubscriber(sub.id)} className="text-red-500 hover:text-red-700">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* ===== COMPOSE TAB ===== */}
      {activeTab === "compose" && composeData && (
        <div className="space-y-4">
          <Button variant="ghost" size="sm" onClick={() => setActiveTab("subscribers")}>
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to Subscribers
          </Button>

          {/* Meta */}
          <Card>
            <CardContent className="pt-6 space-y-4">
              <div className="flex items-center justify-between text-sm text-gray-500">
                <span>Sending to <strong className="text-gray-900">{composeData.subscriber_count}</strong> active subscribers</span>
                {composeData.last_sent_at && (
                  <span>Last sent: {new Date(composeData.last_sent_at).toLocaleDateString()}</span>
                )}
              </div>

              {/* Subject */}
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Subject</label>
                <input
                  type="text"
                  value={editableSubject}
                  onChange={(e) => setEditableSubject(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  placeholder="Newsletter subject line..."
                  data-testid="newsletter-subject-input"
                />
              </div>

              {/* Intro */}
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Introduction</label>
                <textarea
                  value={editableIntro}
                  onChange={(e) => setEditableIntro(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  rows={2}
                  placeholder="Opening message..."
                  data-testid="newsletter-intro-input"
                />
              </div>

              {/* Custom content */}
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Custom Content (optional)</label>
                <textarea
                  value={editableCustom}
                  onChange={(e) => setEditableCustom(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  rows={3}
                  placeholder="Add any personal message or additional content..."
                  data-testid="newsletter-custom-input"
                />
              </div>
            </CardContent>
          </Card>

          {/* Sections Toggles */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Content Sections</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Location */}
              <SectionToggle
                icon={<MapPin className="w-4 h-4 text-cyan-600" />}
                title={composeData.location_changed ? "New Location" : "Current Location"}
                description={composeData.current_location ? `${composeData.current_location.name}, ${composeData.current_location.country}` : "No location set"}
                enabled={editableSections.location}
                onToggle={() => setEditableSections(p => ({...p, location: !p.location}))}
                available={!!composeData.current_location}
                badge={composeData.location_changed ? "NEW" : null}
                testId="section-location"
              />

              {/* Promotions */}
              <SectionToggle
                icon={<Megaphone className="w-4 h-4 text-amber-600" />}
                title="Active Promotions"
                description={`${composeData.active_promotions?.length || 0} active promotions`}
                enabled={editableSections.promotions}
                onToggle={() => setEditableSections(p => ({...p, promotions: !p.promotions}))}
                available={composeData.active_promotions?.length > 0}
                testId="section-promotions"
              />

              {/* Trips */}
              <SectionToggle
                icon={<Calendar className="w-4 h-4 text-emerald-600" />}
                title="New Trip Proposals"
                description={`${composeData.new_trips?.length || 0} new trips`}
                enabled={editableSections.trips}
                onToggle={() => setEditableSections(p => ({...p, trips: !p.trips}))}
                available={composeData.new_trips?.length > 0}
                testId="section-trips"
              />

              {/* Members */}
              <SectionToggle
                icon={<UserPlus className="w-4 h-4 text-purple-600" />}
                title="New Members"
                description={`${composeData.new_members?.length || 0} new members joined`}
                enabled={editableSections.members}
                onToggle={() => setEditableSections(p => ({...p, members: !p.members}))}
                available={composeData.new_members?.length > 0}
                testId="section-members"
              />

              {/* Forum */}
              <SectionToggle
                icon={<MessageSquare className="w-4 h-4 text-blue-600" />}
                title="Hot Forum Topics"
                description={`${composeData.recent_threads?.length || 0} trending threads`}
                enabled={editableSections.threads}
                onToggle={() => setEditableSections(p => ({...p, threads: !p.threads}))}
                available={composeData.recent_threads?.length > 0}
                testId="section-threads"
              />
            </CardContent>
          </Card>

          {/* Preview / Send */}
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setPreview(!preview)}
              className="flex-1"
              data-testid="newsletter-preview-btn"
            >
              {preview ? <Edit3 className="w-4 h-4 mr-1" /> : <Eye className="w-4 h-4 mr-1" />}
              {preview ? "Edit" : "Preview"}
            </Button>
            <Button
              onClick={handleSendNewsletter}
              disabled={sending}
              className="flex-1 bg-cyan-600 hover:bg-cyan-700"
              data-testid="newsletter-send-btn"
            >
              {sending ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
              {sending ? "Sending..." : `Send to ${composeData.subscriber_count} subscribers`}
            </Button>
          </div>

          {/* Preview Area */}
          {preview && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Preview</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="bg-gray-50 rounded-lg p-4 max-h-96 overflow-y-auto">
                  <div className="bg-white rounded-lg shadow-sm overflow-hidden max-w-lg mx-auto">
                    <div className="bg-cyan-500 px-6 py-4">
                      <h2 className="text-white font-semibold text-lg">{editableSubject || "Newsletter"}</h2>
                    </div>
                    <div
                      className="p-6 text-sm"
                      dangerouslySetInnerHTML={{ __html: buildNewsletterHTML() }}
                    />
                    <div className="text-center py-4 border-t text-xs text-gray-400">
                      George Dives &middot; Unsubscribe
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* ===== HISTORY TAB ===== */}
      {activeTab === "history" && (
        <Card>
          <CardHeader>
            <CardTitle>Newsletter History</CardTitle>
          </CardHeader>
          <CardContent>
            {historyLoading ? (
              <p className="text-center py-8 text-gray-500">Loading...</p>
            ) : history.length === 0 ? (
              <p className="text-center py-8 text-gray-500">No newsletters sent yet</p>
            ) : (
              <div className="space-y-3">
                {history.map((item, i) => (
                  <div key={item.id || i} className="flex items-center justify-between p-3 border rounded-lg" data-testid={`newsletter-history-${i}`}>
                    <div>
                      <p className="font-medium text-sm">{item.subject}</p>
                      <p className="text-xs text-gray-500">
                        {new Date(item.sent_at).toLocaleDateString()} at {new Date(item.sent_at).toLocaleTimeString()}
                      </p>
                    </div>
                    <div className="flex items-center gap-3 text-sm">
                      <span className="text-green-600">{item.sent_count} sent</span>
                      {item.failed_count > 0 && <span className="text-red-500">{item.failed_count} failed</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// --- Section Toggle Component ---
const SectionToggle = ({ icon, title, description, enabled, onToggle, available, badge, testId }) => (
  <div
    className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
      enabled && available ? "bg-white border-gray-200" : "bg-gray-50 border-gray-100"
    } ${!available ? "opacity-50" : "cursor-pointer"}`}
    onClick={available ? onToggle : undefined}
    data-testid={testId}
  >
    <div className="flex items-center gap-3">
      {icon}
      <div>
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium">{title}</p>
          {badge && (
            <span className="px-1.5 py-0.5 text-[10px] font-semibold bg-cyan-100 text-cyan-700 rounded">{badge}</span>
          )}
        </div>
        <p className="text-xs text-gray-500">{description}</p>
      </div>
    </div>
    <div className={`w-10 h-5 rounded-full transition-colors ${enabled && available ? "bg-cyan-500" : "bg-gray-300"} relative`}>
      <div className={`w-4 h-4 rounded-full bg-white absolute top-0.5 transition-transform ${enabled && available ? "translate-x-5" : "translate-x-0.5"}`} />
    </div>
  </div>
);

export default NewsletterManager;
